
export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  AED = 'AED'
}

export interface JanuaryBaseline {
  netWorth: number;
  assets: number;
  liabilities: number;
  avgInflow: number;
  avgOutflow: number;
  dateSet: string;
  creditCards?: CreditCard[];
  creditCardLedger?: CreditCardPaymentRecord[];
}

export interface Expense {
  id: string;
  date: string;
  description: string;
  amount: number;
  category: string;
  currency?: Currency; // Per-entry currency support
}

export interface CategoryBudget {
  id?: string; // Database primary key
  category: string;
  budget: number;
  icon?: string; 
}

export interface HistoryPoint {
  date: string;
  value: number;
}

export interface Investment {
  id: string;
  name: string;
  category: string;
  investedAmount: number;
  currentValue?: number; // Added to match DB schema
  date: string;
  annualDividend?: number; // Annual passive income generated
  dividendYield?: number;   // Yield %
  payoutFrequency?: 'Monthly' | 'Quarterly' | 'Annually';
}

export interface InvestmentCategory {
  id: string;
  name: string;
  objective: number;
  currentValue: number;
  icon?: string;
  history: HistoryPoint[];
}

export interface BalanceSheetItem {
  id: string;
  type: 'Asset' | 'Liability';
  name: string;
  value: number;
  date: string;
  isLoan?: boolean;
  monthlyPayment?: number;
  durationMonths?: number;
  remainingMonths?: number;
  startDate: string;
  endDate?: string;
}

export interface CashFlowDetail {
  id: string;
  month: string;
  inflow: number;
  outflow: number;
  isProjected?: boolean;
}

export interface CashFlowEvent {
  id: string;
  description: string;
  amount: number;
  type: 'Inflow' | 'Outflow';
  frequency: 'Once' | 'Monthly' | 'Quarterly' | 'Yearly';
  startDate: string;
  endDate?: string;
}

export interface CreditCard {
  id: string;
  name: string;
  issuer: string;
  last4?: string;
  amountToPay: number;
  minimumPayment?: number;
  deadline: string; // YYYY-MM-DD
  creditLimit?: number;
  apr?: number;
  isPaid?: boolean;
  notes?: string;
}

export interface CreditCardPaymentRecord {
  id: string;
  cardId: string;
  cardName: string;
  issuer: string;
  last4?: string;
  amountPaid: number;
  deadline: string; // YYYY-MM-DD
  monthKey: string; // e.g. '2026-10'
  monthIndex: number; // 0-11
  year: number;
  paidAt: string; // ISO string
  timestampFormatted: string;
  note?: string;
}

export interface CashLedgerEntry {
  id: string;
  monthKey: string; // e.g. '2026-10'
  monthIndex: number; // 0-11
  year: number;
  monthName: string; // e.g. 'Oct' or 'October 2026'
  amount: number; // in base currency (USD)
  timestamp: string; // ISO string
  timestampFormatted: string;
  source: 'Sync Ledger' | 'Manual Input' | 'Balance Sheet Adjustment';
  note?: string;
}

export type Timeframe = '1M' | '3M' | 'YTD' | '1Y' | 'Month' | 'Quarter' | '6 Months' | 'Year' | 'All' | 'Custom';

export interface PassiveIncomeStats {
  totalAnnualPassive: number;
  totalMonthlyPassive: number;
  portfolioYield: number;
  progressToGoal: number;
  assetsCount: number;
  passiveIncomeGoal: number;
}

export interface FinancialFreedomStats {
  lifestyleMonthly: number;
  lifestyleAnnual: number;
  isCustomLifestyle: boolean;
  budgetDefaultMonthly: number;
  totalAnnualPassive: number;
  totalMonthlyPassive: number;
  portfolioValue: number;
  annualWithdrawal4Pct: number;
  monthlyWithdrawal4Pct: number;
  totalAnnualFreedomIncome: number;
  totalMonthlyFreedomIncome: number;
  freedomPercentage: number;
  rawFreedomPercentage: number;
  annualShortfall: number;
  monthlyShortfall: number;
  capitalNeededToInvest: number;
  totalFreedomTargetCapital: number;
  effectiveWealth: number;
  fullLifestyleCapital: number;
  isFreedomAchieved: boolean;
}

export interface FinanceContextType {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  exchangeRates: Record<Currency, number>;
  updateExchangeRate: (currency: Currency, rate: number) => void;
  convertToDisplay: (value: number) => number;
  convertFromInput: (value: number) => number;
  expenses: Expense[];
  addExpenses: (e: Expense[]) => void;
  deleteExpense: (id: string) => void;
  updateExpense: (updated: Expense) => void;
  budgets: CategoryBudget[];
  updateBudget: (category: string, amount: number, icon?: string, oldName?: string) => Promise<void>;
  addCategory: (category: string, budget: number, icon?: string) => Promise<void>;
  investments: Investment[];
  addInvestment: (i: Investment) => void;
  updateInvestment: (i: Investment) => void;
  deleteInvestment: (id: string) => void;
  investmentCategories: InvestmentCategory[];
  addInvestmentCategory: (cat: InvestmentCategory) => Promise<{ success: boolean; error?: string }>;
  updateInvestmentCategory: (cat: InvestmentCategory) => void;
  updateCategoryValue: (id: string, value: number, date: string) => void;
  deleteInvestmentCategory: (id: string) => void;
  deleteCategoryHistoryPoint: (categoryId: string, date: string) => void;
  balanceSheet: BalanceSheetItem[];
  addBalanceSheetItem: (item: BalanceSheetItem) => void;
  updateBalanceSheetItem: (item: BalanceSheetItem) => void;
  deleteBalanceSheetItem: (id: string) => void;
  cashFlows: CashFlowDetail[];
  addCashFlow: (cf: CashFlowDetail) => void;
  cashFlowEvents: CashFlowEvent[];
  addCashFlowEvent: (event: CashFlowEvent) => void;
  updateCashFlowEvent: (event: CashFlowEvent) => void;
  deleteCashFlowEvent: (id: string) => void;
  janBaseline: JanuaryBaseline | null;
  setJanBaseline: (b: JanuaryBaseline) => Promise<void>;
  isDarkMode: boolean;
  setIsDarkMode: (dark: boolean) => void;
  passiveIncomeGoal: number;
  setPassiveIncomeGoal: (goal: number) => void;
  passiveIncomeStats: PassiveIncomeStats;
  lifestyleMonthlyExpenses: number | null;
  setLifestyleMonthlyExpenses: (amt: number | null) => void;
  financialFreedomStats: FinancialFreedomStats;
  creditCards: CreditCard[];
  addCreditCard: (card: Omit<CreditCard, 'id'> | CreditCard) => void;
  updateCreditCard: (card: CreditCard) => void;
  deleteCreditCard: (id: string) => void;
  toggleCreditCardPaid: (id: string) => void;
  creditCardLedger: CreditCardPaymentRecord[];
  recordCreditCardPayment: (card: CreditCard, amountPaid?: number, note?: string) => void;
  deleteCreditCardPaymentRecord: (id: string) => void;
  availableCash: number;
  setAvailableCash: (val: number) => void;
  cashLedgerHistory: CashLedgerEntry[];
  syncCashLedger: (amount?: number, monthIndex?: number, year?: number, note?: string) => { entry: CashLedgerEntry; previousAmount?: number };
  updateCashLedgerEntry: (id: string, newAmount: number, newNote?: string) => void;
  deleteCashLedgerEntry: (id: string) => void;
  getLastHistoricalCashForMonth: (monthIndex: number, year?: number) => number | null;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  availableYears: number[];
}